// ─── src/InvestorLoungeFoundersView.tsx ──────────────────────────────────────
import { useEffect, useState } from "react";
import {
  Building2,
  Download,
  Rocket,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import {
  useInvestorLoungeFounders,
  useInvestorLoungeFounderStats,
  useDeleteInvestorLoungeFounder,
} from "./hooks/useInvestorLounge";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { InvestorLoungeFounder } from "./api/investorLounge";

const LIMIT = 20;

const CSV_HEADERS = [
  "Founder Name",
  "Role",
  "Email",
  "Phone",
  "LinkedIn",
  "Startup",
  "One-Liner",
  "Industry",
  "Stage",
  "HQ",
  "Website",
  "Revenue",
  "Users",
  "Growth",
  "Funding Raised",
  "Meeting Goal",
  "Meeting Unlock",
  "Preferred Investor",
  "Submitted At",
];

function exportCsv(rows: InvestorLoungeFounder[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.founderName,
      r.founderRole,
      r.founderEmail,
      r.founderPhone,
      r.founderLinkedin ?? "",
      r.startupName,
      r.startupOneLiner,
      r.startupIndustry,
      r.startupStage,
      r.startupHq,
      r.startupWebsite ?? "",
      r.tractionRevenue ?? "",
      r.tractionUsers ?? "",
      r.tractionGrowth ?? "",
      r.fundingRaised ?? "",
      r.meetingGoal,
      r.meetingUnlock,
      r.preferredInvestorProfile ?? "",
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `investor_lounge_founders_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function InvestorLoungeFoundersView() {
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<InvestorLoungeFounder | null>(null);

  const { data: stats } = useInvestorLoungeFounderStats();
  const { data: allData } = useInvestorLoungeFounders({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useInvestorLoungeFounders({ page, limit: LIMIT, search, stage });

  const { mutate: deleteRow, isPending: isDeleting } =
    useDeleteInvestorLoungeFounder();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const stageOptions = Object.keys(stats?.byStage ?? {}).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total applications"
          value={stats?.total ?? 0}
          icon={Rocket}
          accent="bg-brand-orange"
        />
        {stageOptions.slice(0, 3).map((s) => (
          <StatCard
            key={s}
            label={s}
            value={stats?.byStage[s] ?? 0}
            icon={Building2}
            accent="bg-brand-teal"
          />
        ))}
      </div>

      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search startup, founder, email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All stages</option>
          {stageOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load applications.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          No founder applications yet.
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Startup</th>
                  <th className="px-4 py-3 font-medium">Founder</th>
                  <th className="px-4 py-3 font-medium">Stage</th>
                  <th className="px-4 py-3 font-medium">HQ</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.startupName}</div>
                      <div className="text-brand-muted text-xs">{r.startupIndustry}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white text-sm">{r.founderName}</div>
                      <div className="text-brand-muted text-xs">{r.founderRole}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted">{r.startupStage}</td>
                    <td className="px-4 py-3 text-brand-muted">{r.startupHq}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this application?"))
                            deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {selected && (
        <FounderDetailModal
          founder={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function FounderDetailModal({
  founder,
  onClose,
}: {
  founder: InvestorLoungeFounder;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/40 animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-brand-border bg-brand-card px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-muted">
              {founder.startupIndustry} · {founder.startupStage}
            </p>
            <h2 className="mt-1 text-xl font-bold text-white">{founder.startupName}</h2>
            <p className="text-sm text-brand-muted">{founder.startupOneLiner}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/40"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Section title="Founder" icon={<User size={12} />}>
            <FieldGrid>
              <Field label="Name" value={founder.founderName} />
              <Field label="Role" value={founder.founderRole} />
              <Field label="Email" value={founder.founderEmail} />
              <Field label="Phone" value={founder.founderPhone} />
              {founder.founderLinkedin && (
                <Field label="LinkedIn" value={founder.founderLinkedin} full />
              )}
            </FieldGrid>
          </Section>

          <Section title="Company" icon={<Building2 size={12} />}>
            <FieldGrid>
              <Field label="HQ" value={founder.startupHq} />
              <Field label="Website" value={founder.startupWebsite ?? "—"} />
            </FieldGrid>
          </Section>

          <Section title="Traction">
            <FieldGrid>
              <Field label="Revenue" value={founder.tractionRevenue ?? "—"} />
              <Field label="Users" value={founder.tractionUsers ?? "—"} />
              <Field label="Recent growth" value={founder.tractionGrowth ?? "—"} full />
              <Field label="Funding raised" value={founder.fundingRaised ?? "—"} full />
            </FieldGrid>
          </Section>

          <Section title="The meeting">
            <div className="space-y-4">
              <LongField label="Goal for the meeting" value={founder.meetingGoal} />
              <LongField label="What a matched investor could unlock" value={founder.meetingUnlock} />
              {founder.preferredInvestorProfile && (
                <LongField label="Preferred investor profile" value={founder.preferredInvestorProfile} />
              )}
            </div>
          </Section>

          <p className="text-xs text-brand-muted">
            Submitted {new Date(founder.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

// Detail helpers reused across both lounge views.
function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-muted mb-3">
        {icon}
        {title}
      </h3>
      <div className="bg-brand-navy/50 rounded-xl p-4">{children}</div>
    </div>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({
  label,
  value,
  full,
}: {
  label: string;
  value: string;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : undefined}>
      <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">
        {label}
      </p>
      <p className="text-white text-sm break-words">{value}</p>
    </div>
  );
}

function LongField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">
        {label}
      </p>
      <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
        {value}
      </p>
    </div>
  );
}
